import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Badge,
  Avatar,
  Menu,
  Typography,
  Divider,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Logout,
  Warning,
  Info,
  CheckCircle,
  Error,
  Business as OrganizationIcon,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import { Notification } from '../../types';

// Empty notifications - will be fetched from API when implemented
const notifications: Notification[] = [];

const SIDEBAR_WIDTH = 220;

// Default reporting periods
const reportingPeriods = [
  { year: 2026, startDate: '2026-01-01', endDate: '2026-12-31', status: 'in_progress' as const },
  { year: 2025, startDate: '2025-01-01', endDate: '2025-12-31', status: 'approved' as const },
  { year: 2024, startDate: '2024-01-01', endDate: '2024-12-31', status: 'approved' as const },
  { year: 2023, startDate: '2023-01-01', endDate: '2023-12-31', status: 'approved' as const },
];

export const Header: React.FC = () => {
  const {
    user,
    facilities,
    selectedPeriod,
    setSelectedPeriod,
    logout
  } = useApp();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElNotif, setAnchorElNotif] = useState<null | HTMLElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const organizationName = user?.organizationName || 'My Organization';

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'error':
        return <Error sx={{ color: '#f44336', fontSize: 18 }} />;
      case 'warning':
        return <Warning sx={{ color: '#ff9800', fontSize: 18 }} />;
      case 'success':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />;
      default:
        return <Info sx={{ color: '#2196f3', fontSize: 18 }} />;
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
        ml: `${SIDEBAR_WIDTH}px`,
        backgroundColor: '#ffffff',
        color: '#1a1a2e',
        borderBottom: '1px solid #e8e8e8',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', py: 0.75, minHeight: '52px !important' }}>
        {/* Left: Organization Name & Period Selector */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Organization Name Display */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: '#f8f9fa',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.75,
              minWidth: 180,
            }}
          >
            <OrganizationIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', lineHeight: 1.2, fontSize: '0.8rem' }}>
                {organizationName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                {facilities.length} {facilities.length === 1 ? 'Facility' : 'Facilities'}
              </Typography>
            </Box>
          </Box>

          {/* Period Selector */}
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={selectedPeriod?.year || 2024}
              onChange={(e) => {
                const period = reportingPeriods.find((p) => p.year === e.target.value);
                if (period) setSelectedPeriod(period);
              }}
              sx={{
                backgroundColor: '#f8f9fa',
                borderRadius: 1.5,
                fontSize: '0.8rem',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': {
                  backgroundColor: '#f0f2f5',
                },
                '& .MuiSelect-select': {
                  py: 0.75,
                },
              }}
            >
              {reportingPeriods.map((period) => (
                <MenuItem key={period.year} value={period.year} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                  FY {period.year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Right: Notifications & User Menu */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Notifications */}
          <IconButton
            size="small"
            onClick={(e) => setAnchorElNotif(e.currentTarget)}
            sx={{
              color: '#555',
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>

          <Popover
            open={Boolean(anchorElNotif)}
            anchorEl={anchorElNotif}
            onClose={() => setAnchorElNotif(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ width: 320, maxHeight: 400 }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  Notifications
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  You have {unreadCount} unread notifications
                </Typography>
              </Box>
              <List sx={{ p: 0 }}>
                {notifications.length === 0 ? (
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemText
                      primary="No notifications"
                      secondary="You're all caught up!"
                      primaryTypographyProps={{ fontSize: '0.8rem' }}
                      secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                  </ListItem>
                ) : (
                  notifications.map((notif) => (
                    <ListItem
                      key={notif.id}
                      sx={{
                        py: 1,
                        borderBottom: '1px solid #f5f5f5',
                        backgroundColor: notif.read ? 'transparent' : '#f9f9f9',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>{getNotificationIcon(notif.type)}</ListItemIcon>
                      <ListItemText
                        primary={notif.title}
                        secondary={
                          <>
                            <Typography variant="body2" sx={{ mb: 0.25, fontSize: '0.75rem' }}>
                              {notif.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {new Date(notif.timestamp).toLocaleString()}
                            </Typography>
                          </>
                        }
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: notif.read ? 400 : 600,
                        }}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Box>
          </Popover>

          {/* User Menu */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 1.5,
              pl: 1.5,
              borderLeft: '1px solid #e8e8e8',
            }}
          >
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.2, color: '#1a1a2e', fontSize: '0.8rem' }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem' }}>
                {user?.role}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => setAnchorElUser(e.currentTarget)}
              sx={{
                p: 0.25,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: '#3b82f6',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  border: '2px solid #e2e8f0',
                }}
              >
                {user?.name?.split(' ').map(n => n.charAt(0)).join('') || 'U'}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={() => setAnchorElUser(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 1.5, py: 1, minWidth: 180 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={logout} sx={{ fontSize: '0.8rem', py: 0.75 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Logout sx={{ fontSize: 18 }} />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
